/**
 * CommandPalette — Ctrl/⌘+K. Go to a view, open a room, run an action, or
 * fall through to full-text search. Every item is something that works.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Sun, MessageSquare, Mail, Users, Video, FolderOpen, Calendar, GitBranch, Settings, Hash, Lock, AtSign,
  Search, Keyboard, SunMoon, Languages, CornerDownLeft, ArrowDownToLine,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { View } from '../../App';
import { useChatStore } from '../../stores';
import { useThemeStore } from '../../stores/themeStore';
import { SUPPORTED_LOCALES } from '../../i18n/config';
import { SHORTCUTS, displayKey, roomListOrder } from '../../lib/shortcuts';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useLicenseFeature } from '../../hooks/useLicenseFeatures';

export interface PaletteItem {
  id: string;
  group: 'go' | 'rooms' | 'people' | 'actions';
  label: string;
  /** Extra words that should match, e.g. the English name of a view */
  keywords?: string;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
  run: () => void;
}

const RECENT_KEY = 'eurion-palette-recent';

function readRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]; } catch { return []; }
}
function remember(id: string) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...readRecent().filter(x => x !== id)].slice(0, 8))); } catch { /* private mode */ }
}

/** 0 = no match. Prefix beats word-start beats substring beats in-order letters. */
export function scoreMatch(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  const s = text.toLowerCase();
  if (s.startsWith(q)) return 4;
  if (s.split(/[\s#@·/-]+/).some(w => w.startsWith(q))) return 3;
  if (s.includes(q)) return 2;
  let i = 0;
  for (const c of s) if (c === q[i]) i++;
  return i === q.length ? 1 : 0;
}

export function rankItems(items: PaletteItem[], query: string, recent: string[] = []): PaletteItem[] {
  if (!query.trim()) {
    const rec = recent.map(id => items.find(i => i.id === id)).filter((i): i is PaletteItem => !!i);
    return [...rec, ...items.filter(i => !recent.includes(i.id))];
  }
  return items
    .map((item, idx) => {
      const s = Math.max(scoreMatch(query, item.label), item.keywords ? scoreMatch(query, item.keywords) - 0.5 : 0);
      const r = recent.indexOf(item.id);
      return { item, idx, s: s > 0 ? s + (r >= 0 ? 0.25 - r * 0.01 : 0) : 0 };
    })
    .filter(x => x.s > 0)
    .sort((a, b) => b.s - a.s || a.idx - b.idx)
    .map(x => x.item);
}

const VIEW_ICONS: Record<string, PaletteItem['icon']> = {
  today: Sun, chat: MessageSquare, mail: Mail, teams: Users, meet: Video, files: FolderOpen, calendar: Calendar, workflows: GitBranch,
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: View) => void;
  onSearch: (query: string) => void;
  onShowShortcuts: () => void;
}

export function CommandPalette(props: CommandPaletteProps) {
  // Mount the body only while open, so the room list is not ranked in the background
  return props.open ? <PaletteBody {...props} /> : null;
}

function PaletteBody({ onClose, onNavigate, onSearch, onShowShortcuts }: CommandPaletteProps) {
  const { t, i18n } = useTranslation();
  const rooms = useChatStore(s => s.rooms);
  const setActiveRoom = useChatStore(s => s.setActiveRoom);
  const updateRoom = useChatStore(s => s.updateRoom);
  const { mode, setMode } = useThemeStore();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const trapRef = useFocusTrap(true);

  useEffect(() => { inputRef.current?.focus(); }, []);
  const mailHidden = useLicenseFeature('mail') === 'hidden';

  const items = useMemo<PaletteItem[]>(() => {
    const goShortcut = (v: View) => SHORTCUTS.find(s => s.view === v)?.keys.map(displayKey).join(' ');
    const views: PaletteItem[] = (['today', 'chat', 'mail', 'teams', 'meet', 'files', 'calendar', 'workflows'] as View[]).filter(v => !(v === 'mail' && mailHidden)).map(v => ({
      id: `go:${v}`, group: 'go', label: t(`nav.${v}`), keywords: v, icon: VIEW_ICONS[v]!, hint: goShortcut(v), run: () => onNavigate(v),
    }));
    const roomItems: PaletteItem[] = roomListOrder(rooms).map(r => {
      const dm = r.type === 'direct';
      const name = dm ? (r.displayName ?? r.name) : r.name;
      return {
        id: `room:${r.id}`, group: dm ? 'people' : 'rooms', label: name, icon: dm ? AtSign : r.isEncrypted ? Lock : Hash,
        hint: r.unreadCount > 0 ? t('palette.unread', { count: r.unreadCount }) : undefined,
        run: () => { onNavigate('chat'); setActiveRoom(r.id); updateRoom(r.id, { unreadCount: 0 }); },
      };
    });
    const next = mode === 'light' ? 'dark' : 'light';
    const actions: PaletteItem[] = [
      { id: 'act:meet', group: 'actions', label: t('palette.startMeeting'), keywords: 'call video meet now', icon: Video, run: () => onNavigate('meet') },
      { id: 'act:theme', group: 'actions', label: t(next === 'light' ? 'palette.lightTheme' : 'palette.darkTheme'), keywords: 'theme dark light mode', icon: SunMoon, run: () => setMode(next) },
      ...SUPPORTED_LOCALES.filter(l => l.code !== i18n.language).map((l): PaletteItem => ({
        id: `act:lang:${l.code}`, group: 'actions', label: t('palette.language', { language: l.label }), keywords: `language språk sprache langue taal język sprog ${l.code}`, icon: Languages,
        run: () => { void i18n.changeLanguage(l.code); },
      })),
      { id: 'act:settings', group: 'actions', label: t('nav.settings'), keywords: 'settings preferences', icon: Settings, hint: [displayKey('Mod'), ','].join(' '), run: () => onNavigate('settings') },
      { id: 'act:shortcuts', group: 'actions', label: t('palette.showShortcuts'), keywords: 'keyboard shortcuts keys', icon: Keyboard, hint: '?', run: onShowShortcuts },
    ];
    const unread = rooms.some(r => r.unreadCount > 0);
    if (unread) actions.unshift({ id: 'act:unread', group: 'actions', label: t('palette.nextUnread'), keywords: 'unread', icon: ArrowDownToLine, hint: ['Alt', 'Shift', '↓'].map(displayKey).join(' '), run: () => {
      const r = roomListOrder(rooms).find(x => x.unreadCount > 0);
      if (r) { onNavigate('chat'); setActiveRoom(r.id); updateRoom(r.id, { unreadCount: 0 }); }
    } });
    return [...views, ...roomItems, ...actions];
  }, [rooms, mode, i18n.language, t, onNavigate, setActiveRoom, updateRoom, setMode, onShowShortcuts, i18n, mailHidden]);

  const results = useMemo(() => {
    const ranked = rankItems(items, query, readRecent());
    // With an empty box, keep it short: recents, views, then a few rooms
    return query.trim() ? ranked.slice(0, 50) : ranked.filter(i => i.group !== 'actions' || readRecent().includes(i.id) || i.id === 'act:meet' || i.id === 'act:unread').slice(0, 24);
  }, [items, query]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const searchIndex = results.length; // the "search everywhere" row is last
  const total = results.length + (query.trim() ? 1 : 0);

  const runAt = (i: number) => {
    if (i === searchIndex && query.trim()) { onClose(); onSearch(query.trim()); return; }
    const item = results[i];
    if (!item) return;
    remember(item.id);
    onClose();
    item.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (total ? (a + 1) % total : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (total ? (a - 1 + total) % total : 0)); }
    else if (e.key === 'Home' && e.ctrlKey) { e.preventDefault(); setActive(0); }
    else if (e.key === 'End' && e.ctrlKey) { e.preventDefault(); setActive(Math.max(total - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); runAt(active); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };

  let lastGroup = '';
  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-start justify-center bg-black/50 px-3 pt-[12vh] animate-fade-in" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label={t('palette.title')}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-nord-3 bg-nord-1 shadow-menu">
        <div className="flex items-center gap-2 border-b border-nord-2 px-3">
          <Search className="h-4 w-4 flex-none text-nord-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={total ? `palette-opt-${active}` : undefined}
            aria-autocomplete="list"
            aria-label={t('palette.title')}
            placeholder={t('palette.placeholder')}
            className="w-full bg-transparent py-3 text-[15px] text-nord-6 placeholder:text-nord-muted focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden rounded bg-nord-0 px-1.5 py-0.5 font-mono text-2xs text-nord-muted sm:inline">{'Esc'}</kbd>
        </div>
        <ul id="palette-list" ref={listRef} role="listbox" aria-label={t('palette.results')} className="max-h-[min(60vh,26rem)] overflow-y-auto p-1.5">
          {results.map((item, i) => {
            const header = !query.trim() && item.group !== lastGroup ? t(`palette.group.${item.group}`) : null;
            lastGroup = item.group;
            const Icon = item.icon;
            return (
              <li key={item.id} role="presentation">
                {header && <div role="presentation" className="px-2.5 pb-1 pt-2 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-nord-muted">{header}</div>}
                <div
                  id={`palette-opt-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={i === active}
                  onMouseMove={() => { if (active !== i) setActive(i); }}
                  onClick={() => runAt(i)}
                  className={clsx('flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm', i === active ? 'bg-nord-2 text-nord-6' : 'text-nord-4')}
                >
                  <Icon className="h-4 w-4 flex-none text-nord-8" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint && <span className="flex-none font-mono text-[11px] text-nord-muted">{item.hint}</span>}
                </div>
              </li>
            );
          })}
          {query.trim() && (
            <li role="presentation">
              <div id={`palette-opt-${searchIndex}`} data-index={searchIndex} role="option" aria-selected={active === searchIndex}
                onMouseMove={() => setActive(searchIndex)} onClick={() => runAt(searchIndex)}
                className={clsx('flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm', active === searchIndex ? 'bg-nord-2 text-nord-6' : 'text-nord-4')}>
                <Search className="h-4 w-4 flex-none text-nord-8" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{t('palette.searchFor', { query: query.trim() })}</span>
              </div>
            </li>
          )}
        </ul>
        <div className="flex items-center gap-4 border-t border-nord-2 px-3 py-2 font-mono text-[11px] text-nord-muted">
          <span>↑↓ {t('palette.move')}</span>
          <span><CornerDownLeft className="inline h-3 w-3" aria-hidden="true" /> {t('palette.open')}</span>
          <span className="ml-auto hidden sm:inline">{t('palette.rankedHere')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
